import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Stack, FormControlLabel, Switch, styled,
} from "@mui/material";
import { CustomButton } from "@/components/custom/custom-button";
import { CustomErrorMessage } from "@/components/custom/custom-error-message";
import { FormSelectInput } from "@/components/custom/custom-form-select-input";
import { FormTextInput } from "@/components/custom/custom-form-text-input";
import { FormTextArea } from "@/components/custom/custom-form-textarea";
import { showToast } from "@/redux/actions/toast-actions";
import { interceptionRuleSchema } from "@/schemas/payout.schema";
import { useCreateInterceptionRule, useUpdateInterceptionRule } from "@/hooks/use-interception-rules";
import { RULE_TYPE_OPTIONS } from "../data";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": { borderRadius: "12px", padding: theme.spacing(2), maxWidth: 480, width: "100%" },
}));

const RuleFormDialog = ({ open, rule, onClose }) => {
  const dispatch = useDispatch();
  const isEdit = Boolean(rule);
  const createMutation = useCreateInterceptionRule();
  const updateMutation = useUpdateInterceptionRule();
  const mutation = isEdit ? updateMutation : createMutation;

  const formik = useFormik({
    initialValues: {
      ruleType: rule?.ruleType ?? "",
      amountThreshold: rule?.amountThreshold ?? "",
      startTime: rule?.startTime ?? "",
      endTime: rule?.endTime ?? "",
      description: rule?.description ?? "",
      isEnabled: rule?.isEnabled ?? true,
    },
    enableReinitialize: true,
    validationSchema: interceptionRuleSchema,
    validateOnMount: true,
    onSubmit: (values) => {
      const payload = { ...values, amountThreshold: Number(values.amountThreshold) };
      const action = isEdit
        ? updateMutation.mutateAsync({ id: rule.id, data: payload })
        : createMutation.mutateAsync(payload);

      action
        .then(() => {
          dispatch(showToast(isEdit ? "Rule updated" : "Rule created", "success"));
          onClose();
        })
        .catch((err) => dispatch(showToast(err?.message ?? "Failed to save rule", "error")));
    },
  });

  useEffect(() => {
    if (open) mutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (mutation.isPending) return;
    formik.resetForm();
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
        <DialogTitle sx={{ p: 0, fontSize: "1.1rem", fontWeight: 600 }}>
          {isEdit ? "Edit Interception Rule" : "New Interception Rule"}
        </DialogTitle>

        {mutation.isError && <CustomErrorMessage message={mutation.error?.message ?? "Failed to save rule"} />}

        <Stack spacing={2}>
          <FormSelectInput formik={formik} name="ruleType" label="Rule Type" options={RULE_TYPE_OPTIONS} isDisabled={isEdit} />
          <FormTextInput formik={formik} name="amountThreshold" label="Amount Threshold" type="number" />
          <Stack direction="row" spacing={2}>
            <FormTextInput formik={formik} name="startTime" label="Start Time" type="time" />
            <FormTextInput formik={formik} name="endTime" label="End Time" type="time" />
          </Stack>
          <FormTextArea formik={formik} name="description" label="Description (optional)" rows={2} />
          <FormControlLabel
            control={
              <Switch
                checked={formik.values.isEnabled}
                onChange={(e) => formik.setFieldValue("isEnabled", e.target.checked)}
              />
            }
            label="Enabled"
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2, pt: 1, pb: 1, gap: 1 }}>
        <CustomButton variantType="outline" size="small" onClick={handleClose} disabled={mutation.isPending} sx={{ px: 2 }}>
          Cancel
        </CustomButton>
        <CustomButton
          size="small"
          disabled={!formik.isValid || mutation.isPending}
          isLoading={mutation.isPending}
          onClick={formik.handleSubmit}
          sx={{ px: 2 }}
        >
          {mutation.isPending ? "Saving..." : "Save"}
        </CustomButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default RuleFormDialog;